import * as React from 'react';
import {
    DirectionalHint,
    classNamesFunction,
    VerticalDivider,
    getRTL,
    FocusZoneDirection,
    elementContains,
    IContextualMenuListProps,
    ContextualMenu,
    FontIcon,
    getId,
    IFocusZone,
    ContextualMenuItemType,
} from '@fluentui/react';
import { IGroupedComboBoxButtonProps } from './GroupedComboBoxButton.types';
import {
    DropDownContext,
    IGroupedComboBoxButtonIcon,
    IItemCommands,
    SubmenuContext,
} from 'grouped-combobox-core';
import { useBoolean, useConst, useId } from '@fluentui/react-hooks';
import { IGroupedComboBoxItemStyleProps, IGroupedComboBoxItemStyles } from '../GroupedComboBox';
import {
    GROUPED_COMBOBOX_ITEM_HEIGHT,
    primaryButtonRightSideContainerStyle,
    submenuDividerHeight,
    submenuDividerTopBottomMargin,
    submenuStyles,
    submenuTopBottomPadding,
} from './GroupedComboBoxButton.styles';
import { RenderTooltip } from '../utils/renderTooltip';
import { assert } from 'textfield-with-action';
import {
    defaultOnRenderIcon,
    defaultOnRenderLabel,
    renderHiddenElement,
    renderSubmenuInternalList,
} from './renderHelpers';
import { isMouseEventLocatedInElement, isTouchEvent } from '../utils';
import { useAnimateEntrance, useAnimateSubmenuItemExit } from './animationHooks';

const getClassNames = classNamesFunction<
    IGroupedComboBoxItemStyleProps,
    IGroupedComboBoxItemStyles
>();

function disableMouseEvent(ev: React.MouseEvent<HTMLElement>): void {
    ev.preventDefault();
    ev.stopPropagation();
}

const enum SubMenuState {
    Closed,
    OpenByKeyboard,
    OpenByOther,
}

const isSafari = /apple/i.test(navigator.vendor);

// In Safari, VoiceOver doesn't read aria-label values of individual elements inside the
// 'menuitem' parent div element. Therefore we have to set the aria-label value manually.
function constructAriaLabel(...labels: unknown[]): string | undefined {
    if (!isSafari) {
        return undefined;
    }
    const arialLabel = labels.filter((label) => !!label).join(' ');
    return arialLabel;
}

/**
 * The unstyled base control. All consumers should use {@link GroupedComboBoxButton}
 *
 * @param props - The props passed to this control for rendering
 */
export const GroupedComboBoxButtonBase: React.FunctionComponent<IGroupedComboBoxButtonProps> = (
    props: IGroupedComboBoxButtonProps
) => {
    const {
        className,
        item,
        reserveSpaceForIcon = false,
        isSubButton,
        styles,
        theme,
        parentGroupKey,
        selectedKey,
        animateEntrance = false,
        itemHeight = GROUPED_COMBOBOX_ITEM_HEIGHT,
    } = props;

    const {
        text,
        secondaryText,
        disabled,
        sections,
        onRenderIcon = defaultOnRenderIcon,
        onRenderLabel = defaultOnRenderLabel,
        tooltip,
    } = item;

    const [isPressed, setIsPressed] = useBoolean(false);
    const [isHover, setIsHover] = React.useState(false);
    const [isPrimaryButtonHover, setIsPrimaryButtonHover] = React.useState(false);
    const [isActive, setIsActive] = React.useState(false);
    const [subMenuState, setSubmenuState] = React.useState<number>(SubMenuState.Closed);
    const isSubmenuOpen = subMenuState !== SubMenuState.Closed;
    const [submenuSections, setSubmenuSections] = React.useState(sections);

    const {
        onItemClick,
        onItemSecondaryAction,
        onItemMouseEnter,
        onItemMouseLeave,
        onItemGotFocus,
        onItemLostFocus,
        submenuMaxWidth,
        submenuMaxHeight,
        onSubmenuOpen,
        onSubmenuClose,
        iconResources,
        onRenderTooltip = RenderTooltip,
        onRenderMenu,
        onTouchStart,
        getItemTooltip,
    } = React.useContext(DropDownContext);

    const subMenuContext = React.useContext(SubmenuContext);
    const labelContainerId = useId('labelcontainer');

    const iconRef = React.useRef<IGroupedComboBoxButtonIcon>(null);

    const isIconActive = isHover || isActive;
    const [iconElement, iconDescription, actionDescription] = onRenderIcon({
        item: item,
        onRenderTooltip,
        isActive: isIconActive,
        mouseLeaveBoundingBox: () => {
            setIsHover(false);
        },
        refreshContainingMenu: subMenuContext.refreshContainingMenu,
        labelElementId: labelContainerId,
        componentRef: iconRef,
    });

    const iconContainerRef = React.useRef<HTMLDivElement>(null);
    const menuCommands = useConst<IItemCommands>({
        openMenu: () => setSubmenuState(SubMenuState.OpenByOther),
        closeMenu: () => setSubmenuState(SubMenuState.Closed),
        setIsHover: (hover: boolean) => {
            setIsHover(hover);
            if (!hover) {
                iconRef.current?.setHover(hover);
            }
        },
    });

    const onRenderSubmenuList = React.useCallback(
        (
            props?: IContextualMenuListProps,
            defaultRender?: (props?: IContextualMenuListProps) => JSX.Element | null
        ) => {
            if (onRenderMenu) {
                return onRenderMenu({ isSubmenu: true }, () =>
                    renderSubmenuInternalList(setSubmenuSections, props, defaultRender)
                );
            } else {
                return renderSubmenuInternalList(setSubmenuSections, props, defaultRender);
            }
        },
        [onRenderMenu]
    );

    const submenuId = useConst(() => getId('grouped-combobox-button-submenu'));
    const subMenuFocusZoneRef = React.useRef<IFocusZone>(null);

    const onSubmenuDismissed = React.useCallback(() => {
        setIsHover(false);
        onSubmenuClose?.();
    }, [setIsHover, onSubmenuClose]);

    const menuItems = useAnimateSubmenuItemExit(
        isSubmenuOpen,
        itemHeight,
        submenuSections,
        selectedKey,
        styles
    );

    const subMenuButtonElementRef = React.useRef<HTMLDivElement>(null);

    const mouseOverCallback = React.useCallback(
        (ev: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
            setIsHover(true);

            const mouseInTextContainer = isMouseEventLocatedInElement(ev, primaryButtonRef);
            const mouseInIconContainer = isMouseEventLocatedInElement(ev, iconContainerRef);

            setIsPrimaryButtonHover(
                mouseInTextContainer ||
                    // If the icon renderer calls prevent default, don't consider it as the primary button
                    (!ev.isDefaultPrevented() && mouseInIconContainer)
            );

            // Check if the mouse is over the menu button.
            const mouseInMenuButton =
                ev.target == subMenuButtonElementRef.current ||
                isMouseEventLocatedInElement(ev, subMenuButtonElementRef);

            // If mouse is on the menu button, send button reference so GroupedComboBoxDropdown knows to open the submenu.
            onItemMouseEnter?.(
                item,
                ev,
                mouseInMenuButton ? menuCommands : undefined,
                isSubButton,
                parentGroupKey
            );
        },
        [
            setIsHover,
            onItemMouseEnter,
            item,
            isSubButton,
            parentGroupKey,
            setIsPrimaryButtonHover,
            menuCommands,
        ]
    );

    const mouseLeaveCallback = React.useCallback(
        (ev: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
            if (ev.target instanceof Node && rootRef.current?.contains(ev.target)) {
                setIsHover(false);
                setIsPrimaryButtonHover(false);

                onItemMouseLeave?.(item, ev);
            }
        },
        [setIsHover, onItemMouseLeave, item]
    );

    assert(theme);

    // When a button should be selected, 'selectedKey' value will be set. When it's not selected, 'selectedKey' is undefined.
    const isSelected = !!selectedKey;

    const showButton = useAnimateEntrance(animateEntrance);

    const classNames = getClassNames(styles, {
        theme: theme,
        className,
        disabled: disabled || false,
        isHover: isHover || isActive,
        isPressed,
        reserveSpaceForIcon,
        hasIcon: iconElement !== null,
        submenuOpen: isSubmenuOpen,
        isSelected,
        isSubButton,
        isPrimaryButtonHover,
        showButton,
        itemHeight,
    });

    const rtl = getRTL(theme);

    const onRootKeyDown = React.useCallback(
        (ev: React.KeyboardEvent<HTMLDivElement>) => {
            if (disabled) {
                return;
            }

            if (
                (ev.key === 'ArrowLeft' && rtl) ||
                (ev.key === 'ArrowRight' && !rtl) ||
                (ev.altKey && ev.key === 'ArrowDown')
            ) {
                if (!isSubButton) {
                    // when opening the submenu via keyboard, we want to transfer focus on the submenu
                    setSubmenuState(SubMenuState.OpenByKeyboard);
                    onSubmenuOpen?.(item, menuCommands, parentGroupKey);
                }

                ev.stopPropagation();
                ev.preventDefault();
            } else if (ev.shiftKey && ev.key === 'Enter' && onItemSecondaryAction) {
                if (onItemSecondaryAction?.(item, subMenuContext.refreshContainingMenu, ev)) {
                    iconRef.current?.refresh();
                }
                ev.stopPropagation();
                ev.preventDefault();
            } else if (ev.key === 'Enter' || ev.key === ' ' || ev.key === 'Tab') {
                onItemClick?.(item, ev);
                ev.stopPropagation();

                if (ev.key !== 'Tab') {
                    ev.preventDefault();
                }
            }
        },
        [
            disabled,
            rtl,
            onItemSecondaryAction,
            isSubButton,
            setSubmenuState,
            onSubmenuOpen,
            item,
            menuCommands,
            parentGroupKey,
            subMenuContext.refreshContainingMenu,
            onItemClick,
        ]
    );

    const onMainContainerMouseUp = React.useCallback(
        (ev?: React.MouseEvent<unknown> | React.KeyboardEvent<unknown>) => {
            if (disabled) {
                return;
            }

            setIsPressed.setFalse();
            onItemClick?.(item, ev);
            ev?.stopPropagation();
        },
        [disabled, setIsPressed, onItemClick, item]
    );

    const rootRef = React.useRef<HTMLDivElement>(null);

    const onFocusCallback = React.useCallback(
        (event: React.FocusEvent<HTMLDivElement>) => {
            setIsActive(true);

            // The focus event bubbles; if an L2 item receives focus, the parent L1 item
            // also receives onFocus event. Thus we call elementContains() to check
            // if this current button actually received focus before calling onItemGotFocus().
            if (
                onItemGotFocus &&
                rootRef.current &&
                elementContains(rootRef.current, event.target, false)
            ) {
                onItemGotFocus(item, event);
            }
        },
        [onItemGotFocus, item]
    );

    const onBlurCallback = React.useCallback(
        (event: React.FocusEvent<HTMLDivElement>) => {
            setIsActive(false);
            onItemLostFocus?.(item, event);
        },
        [onItemLostFocus, item]
    );

    const onItemTouchStart = React.useCallback(() => {
        onTouchStart?.(item, menuCommands, isSubButton, parentGroupKey);
    }, [onTouchStart, item, menuCommands, isSubButton, parentGroupKey]);

    const onSubmenuButtonClick = React.useCallback(
        (ev: React.MouseEvent<HTMLElement>) => {
            const { nativeEvent } = ev;

            if (isTouchEvent(nativeEvent)) {
                setIsHover(true);

                onItemMouseEnter?.(item, ev, menuCommands, false, parentGroupKey);

                ev.stopPropagation();
            }
        },
        [onItemMouseEnter, item, menuCommands, parentGroupKey]
    );

    const splitButton = !!submenuSections;

    const primaryButtonRef = React.useRef<HTMLSpanElement>(null);

    const onMainContainerMouseDown = React.useCallback(
        (ev: React.MouseEvent<HTMLDivElement>) => {
            setIsPressed.setTrue();
            ev.preventDefault();
        },
        [setIsPressed]
    );

    // Allows the callout to provide better positioning when adding/removing items
    const finalCalloutHeight = React.useMemo(
        () =>
            isSubmenuOpen && menuItems.length > 0
                ? menuItems.reduce((total, item) => {
                      const { itemType } = item;
                      if (itemType === ContextualMenuItemType.Divider) {
                          return total + submenuDividerTopBottomMargin * 2 + submenuDividerHeight;
                      }
                      return total + itemHeight;
                  }, submenuTopBottomPadding * 2)
                : 0,
        [isSubmenuOpen, itemHeight, menuItems]
    );

    // Main container comprises of the primary button and chevron menu button.
    const mainContainer = (
        <div
            className={classNames.mainContainer}
            role="button"
            onMouseDown={onMainContainerMouseDown}
            onMouseUp={onMainContainerMouseUp}
            onContextMenu={disableMouseEvent}
            tabIndex={-1}
        >
            <span ref={primaryButtonRef} className={classNames.textContainer}>
                <span id={labelContainerId} className={classNames.labelContainer} aria-label={text}>
                    {onRenderLabel({
                        item,
                        labelClassName: classNames.labelText,
                        theme,
                        indicatorClassName: classNames.indicator,
                        indicatorIconName: iconResources?.indicatorName,
                    })}
                </span>
                <div style={primaryButtonRightSideContainerStyle}>
                    {secondaryText && (
                        <span className={classNames.secondaryText}>{secondaryText}</span>
                    )}
                    {splitButton && (
                        <VerticalDivider
                            aria-hidden="true"
                            styles={{ divider: [classNames.divider] }}
                        />
                    )}
                </div>
            </span>

            {splitButton && (
                <>
                    <div
                        ref={subMenuButtonElementRef}
                        aria-hidden={true}
                        className={classNames.menuButton}
                        onMouseDown={disableMouseEvent}
                        onMouseUp={disableMouseEvent}
                        onClick={onSubmenuButtonClick}
                    >
                        <FontIcon
                            iconName={
                                rtl
                                    ? iconResources?.chevronLeftName ?? 'ChevronLeft-svg'
                                    : iconResources?.chevronRightName ?? 'ChevronRight-svg'
                            }
                            className={classNames.menuIcon}
                        />
                    </div>

                    {isSubmenuOpen && (
                        <ContextualMenu
                            id={submenuId}
                            isBeakVisible={false}
                            items={menuItems}
                            shouldFocusOnMount={subMenuState === SubMenuState.OpenByKeyboard}
                            directionalHint={DirectionalHint.rightTopEdge}
                            directionalHintForRTL={DirectionalHint.leftTopEdge}
                            calloutProps={{
                                calloutMaxHeight: submenuMaxHeight,
                                calloutMaxWidth: submenuMaxWidth,
                                finalHeight: finalCalloutHeight,
                            }}
                            onRenderMenuList={onRenderSubmenuList}
                            styles={submenuStyles}
                            isSubMenu={true}
                            onDismiss={menuCommands.closeMenu}
                            onMenuDismissed={onSubmenuDismissed}
                            target={subMenuButtonElementRef}
                            focusZoneProps={{
                                componentRef: subMenuFocusZoneRef,
                                direction: FocusZoneDirection.vertical,
                                checkForNoWrap: true,
                            }}
                        />
                    )}
                </>
            )}
        </div>
    );

    const iconId = useId('icon');

    const shouldRenderHiddenActionElement = iconElement && isActive && actionDescription;
    const actionDescriptionId = shouldRenderHiddenActionElement ? iconId + '-action' : undefined;

    const iconContainer =
        iconElement !== null ? (
            <>
                <div
                    ref={iconContainerRef}
                    id={iconId}
                    aria-label={iconDescription}
                    className={classNames.icon}
                >
                    {iconElement}
                </div>

                {shouldRenderHiddenActionElement &&
                    actionDescriptionId &&
                    renderHiddenElement(actionDescriptionId, actionDescription)}
            </>
        ) : undefined;

    const root = (
        <div
            ref={rootRef}
            role="menuitem"
            aria-label={constructAriaLabel(iconDescription, text, secondaryText)}
            aria-describedby={actionDescriptionId}
            aria-expanded={splitButton ? isSubmenuOpen : undefined}
            aria-haspopup={splitButton ? 'menu' : undefined}
            aria-owns={isSubmenuOpen ? submenuId : undefined}
            aria-disabled={disabled}
            className={classNames.root}
            onMouseOver={mouseOverCallback}
            onMouseLeave={mouseLeaveCallback}
            onKeyDown={onRootKeyDown}
            onFocus={onFocusCallback}
            onBlur={onBlurCallback}
            tabIndex={-1}
            data-is-focusable={true}
            onTouchStart={onItemTouchStart}
        >
            {iconContainer}
            {mainContainer}
        </div>
    );

    // We show tooltip when either of the following conditions is met:
    //   + tooltip is set and not null.
    //   + tooltip is NOT set but getItemTooltip is provided.
    const showTooltip = tooltip || (tooltip === undefined && getItemTooltip);

    const rootWithTooltip = showTooltip
        ? onRenderTooltip({
              targetElement: rootRef,
              tooltip:
                  tooltip ??
                  (getItemTooltip ? (): string => getItemTooltip(item, isActive) : undefined),
              showTooltipOnRight: isSubButton ?? false,
              mainContent: root,
              hoverElement: primaryButtonRef.current ?? undefined,
              hostClassName: classNames.rootTooltipHost,
          })
        : root;

    return rootWithTooltip;
};

GroupedComboBoxButtonBase.displayName = 'GroupedComboBoxButtonBase';
